import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import CartographyPanel from 'components/map/CartographyPanel'
import { LAYERS } from 'components/map/constants/MapLayersConstants'
import { intersectionWith, compact, isUndefined } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'

const CatchmentPointsMap = ({
    catchmentPoints,
    specificFiles = [],
}) => {
    const {
        catchment,
        piezometers,
        qualitometers,
        installations,
        stationWatershed,
        watershed1,
        watershed2,
        watershed3,
        watershed4,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        piezometers: store.PiezometryReducer.piezometersLight,
        qualitometers: store.QualityReducer.qualitometersLight,
        installations: store.InstallationReducer.installationsLight,
        stationWatershed: store.WatershedReducer.stationWatershed,
        watershed1: store.WatershedReducer.watershed1,
        watershed2: store.WatershedReducer.watershed2,
        watershed3: store.WatershedReducer.watershed3,
        watershed4: store.WatershedReducer.watershed4,
    }), shallowEqual)

    const comparator = (station, point) => station.code === point.codeWithoutDesignation
    const piezo = useMemo(() => intersectionWith(piezometers, catchmentPoints, comparator), [catchmentPoints, piezometers])
    const qualito = useMemo(() => intersectionWith(qualitometers, catchmentPoints, comparator), [catchmentPoints, qualitometers])
    const installation = useMemo(() => intersectionWith(installations, catchmentPoints, comparator), [catchmentPoints, installations])

    const aacFile = useMemo(() => specificFiles.find(f => !isUndefined(f?.aacFromSandre)), [specificFiles])
    const ftpLayer = useMemo(() => aacFile?.aacFromSandre ? LAYERS.AAC_SANDRE : LAYERS.AAC_UPLOADED, [aacFile])
    const aacLayer = useMemo(() => isUndefined(aacFile) ? undefined : ftpLayer, [aacFile, ftpLayer])

    const watermassLayer = useMemo(() => specificFiles.find(f => !isUndefined(f?.watermassFromSandre)) ? LAYERS.WATERMASS : undefined, [specificFiles])

    const watershedsLayers = useMemo(() => compact([
        stationWatershed?.code ? LAYERS.STATION_WATERSHED : undefined,
        watershed1?.code ? LAYERS.STATION_WATERSHED_1 : undefined,
        watershed2?.code ? LAYERS.STATION_WATERSHED_2 : undefined,
        watershed3?.code ? LAYERS.STATION_WATERSHED_3 : undefined,
        watershed4?.code ? LAYERS.STATION_WATERSHED_4 : undefined,
    ]), [stationWatershed, watershed1, watershed2, watershed3, watershed4])

    return (
        <CartographyPanel
            layers={compact([LAYERS.STATIONS_POINTS, aacLayer, watermassLayer, ...watershedsLayers])}
            station={catchment}
            stationsPoints={[
                ...piezo,
                ...qualito,
                ...installation,
            ]}
            stationsPanelTitle={i18n.stations}
            heightToSubstract={450}
            stationFiles={compact([aacFile])}
            watermass={catchment.meCode?.replace('FR', '')}
        />
    )
}

CatchmentPointsMap.propTypes = {
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    specificFiles: PropTypes.arrayOf(PropTypes.shape({
        active: PropTypes.string,
        code: PropTypes.number,
        date: PropTypes.number,
        fileType: PropTypes.string,
        isAllDataName: PropTypes.bool,
        aacFromSandre: PropTypes.bool,
        name: PropTypes.string,
        shared: PropTypes.bool,
        shortName: PropTypes.string,
        stationCode: PropTypes.string,
        stationType: PropTypes.string,
        type: PropTypes.string,
        updateDate: PropTypes.number,
        url: PropTypes.string,
    })),
}

export default CatchmentPointsMap